import React from "react";
import { useTranslation } from "react-i18next";

import { UTP2Groups, UTP2GroupedConditions, UTP2Substations, CableNaming } from "../../shared/enums/UTP2";

import { Row, Col } from "react-bootstrap";
import { FormRow } from "../../shared/FormLayout/FormRow";
import { Card } from "../../shared/Card";
import { CheckboxList } from "../CheckboxList";

import { Divider } from "@mui/material";
import { Dropdown, DropdownChangeParams } from "primereact/dropdown";

import { WorkflowSubstationSelector } from "../WorkflowSubstationSelector";
import type { WorkflowReducer } from "../workflowReducer";
import { CustomQuestions } from "./CustomQuestions";

interface Props {
  workflowReducer: WorkflowReducer;
  editMode?: boolean;
  onConditionToggle?: (condition: UTP2GroupedConditions) => void;
  onCableNamingChange?: (value: CableNaming) => void;
  companyName?: string;
  siteName?: string;
  onCompanyNameChange?: (companyName: string | undefined) => void;
  onSiteNameChange?: (siteName: string | undefined) => void;
}

export function SubstationTab(props: Props) {
  const { t } = useTranslation();

  const {
    addCustomQuestionGroup,
    removeCustomQuestionGroup,
    editCustomQuestionGroupName,
    addCustomQuestionField,
    editCustomQuestionField,
    removeCustomQuestionField,
    state: workflowState,
  } = props.workflowReducer;

  function handleCableNamingDropdownChange(e: DropdownChangeParams) {
    props.onCableNamingChange?.(e.value);
  }

  function handleCompanyNameChange(companyName: string | undefined) {
    if (props.onCompanyNameChange) props.onCompanyNameChange(companyName);
  }

  function handleSiteNameChange(siteName: string | undefined) {
    if (props.onSiteNameChange) props.onSiteNameChange(siteName);
  }

  return (
    <React.Fragment>
      <Row>
        <Col>
          <Card title={t("UTP2.conditions")} className="mb-3">
            {workflowState.conditions && (
              <CheckboxList
                subheader={t("UTP2.conditionsChecks")}
                editable={props.editMode}
                listItems={workflowState.conditions?.map((condition) => {
                  return {
                    fieldName: condition.key.toString(),
                    label: t(`UTP2.groupedConditions.${condition.key}`),
                    checked: condition.checked,
                    onToggle: () => props.onConditionToggle?.(condition.key),
                  };
                })}
              />
            )}
          </Card>
        </Col>
        <Col lg={6}>
          <Card title={t("substations.substationDetails")} className="mb-3">
            <WorkflowSubstationSelector
              workflowReducer={props.workflowReducer}
              editMode={props.editMode}
              customerId={workflowState.workflow.customerId}
              selectedSubstationId={workflowState.workflow.workflowDetail?.substation_id}
              companyName={props.companyName}
              siteName={props.siteName}
              onCompanyNameChange={handleCompanyNameChange}
              onSiteNameChange={handleSiteNameChange}
            />
            <Divider />
            <FormRow
              label={t(`UTP2.${UTP2Groups.Substation}.${UTP2Substations.CableNaming}`)}
              value={
                props.editMode ? (
                  <Dropdown
                    value={workflowState.cableNaming}
                    onChange={handleCableNamingDropdownChange}
                    options={Object.values(CableNaming).map((cableName: string) => {
                      return {
                        label: t(`UTP2.cableNaming.${cableName}`),
                        value: cableName,
                      };
                    })}
                  />
                ) : workflowState.cableNaming ? (
                  t(`UTP2.cableNaming.${workflowState.cableNaming}`)
                ) : (
                  ""
                )
              }
            />
          </Card>
          <Card title={t("customQuestions.customQuestions")} className="mb-3">
            <CustomQuestions
              customerId={workflowState.workflow.customerId}
              editMode={props.editMode}
              customQuestionGroups={workflowState.customQuestionsGroups}
              onCustomQuestionGroupNameChange={editCustomQuestionGroupName}
              onCustomQuestionGroupRemoveClick={removeCustomQuestionGroup}
              onCustomQuestionGroupAddClick={addCustomQuestionGroup}
              onQuestionFieldAddClick={addCustomQuestionField}
              onQuestionFieldEditClick={editCustomQuestionField}
              onQuestionFieldRemoveClick={removeCustomQuestionField}
            />
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
}
